/** Formatting shared across the SPA, so one number never renders two ways on two screens. */

export function money(amountMinor: number, currency: string, locale = 'en'): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: amountMinor % 100 === 0 ? 0 : 2,
    }).format(amountMinor / 100)
  } catch {
    return `${currency} ${(amountMinor / 100).toFixed(2)}`
  }
}

/**
 * Money ranges are the house style for anything the demand model produced. A single number
 * reads as a measurement; a range reads as an estimate, which is what it is (SW-801).
 */
export function moneyRange(lowMinor: number, highMinor: number, currency: string): string {
  if (lowMinor === 0 && highMinor === 0) return '-'
  return `${money(lowMinor, currency)}–${money(highMinor, currency)}`
}

export function number(value: number): string {
  return new Intl.NumberFormat().format(value)
}

export function percent(value: number, digits = 1): string {
  return `${(value * 100).toFixed(digits)}%`
}

export function relativeDate(epochSeconds: number): string {
  const diffDays = Math.round((epochSeconds * 1000 - Date.now()) / 86_400_000)
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
  if (Math.abs(diffDays) < 1) return 'today'
  if (Math.abs(diffDays) < 30) return formatter.format(diffDays, 'day')
  return new Date(epochSeconds * 1000).toLocaleDateString()
}

export function shortDate(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  })
}

/** Confidence to a Polaris badge tone. Low confidence must *look* low. */
export function confidenceTone(confidence: string): 'success' | 'info' | 'caution' | 'neutral' {
  switch (confidence) {
    case 'high':
      return 'success'
    case 'medium':
      return 'info'
    case 'low':
      return 'caution'
    default:
      return 'neutral'
  }
}
